import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Optional, Output, ViewChild } from '@angular/core';

import { firstValueFrom, Observable, Subscription, catchError, combineLatest, filter, map, of, switchMap, take, throwError } from 'rxjs';

import { Am5Chart } from '../../../../Common/Charts/models/am5-chart.model';
import { Am5DateValueXYChart } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { ChartLoadingOptionsEnum } from '../../enums/chart-loading-options.enum';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { ReportsSeriesDataMergerService } from '../../../Reports/services/reports-series-data-merger.service';
import { ReportsSeriesDataGrouperService } from '../../../Reports/services/reports-series-data-grouper.service';
import { ChartAnnotationIndicatorsService } from '../../../../Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-indicators.service';
import { ChartAnnotationMarkerTypesEnum } from '../../../../Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-marker-types.enum';
import { Am5ChartTooltipService } from '../../../../Common/Charts/services/am5-chart-tooltip.service';


@Component({
    selector: 'agpr-am5-chart',
    templateUrl: './am5-chart.component.html',
    styleUrls: ['./am5-chart.component.scss'],
    providers: [Am5ChartTooltipService],
    standalone: false
})
export class Am5ChartComponent implements AfterViewInit, OnDestroy {
    @Input() id: string = null;
    @Input() smallIndicator: boolean = false;
    @Input() showLoadingUntil: ChartLoadingOptionsEnum = ChartLoadingOptionsEnum.all;
    @Input()
    set chart(chart: Am5Chart) {
        if (this._chart && this._chart !== chart) {
            this.destroyOldChart();
        }
        this._chart = chart;
        this.initializeChart();
        this.chartId = chart?.config?.id;
    }
    get chart() {
        return this._chart;
    }
    @Input() series: ReportSeriesModel[];
    @Output() noData: EventEmitter<boolean> = new EventEmitter(false); // this is used in backend error case
    @Output() noContent: EventEmitter<boolean> = new EventEmitter(); // this is used when backend returns zero results
    @Output() tooltipLinkClick: EventEmitter<any> = new EventEmitter();

    protected _chart: Am5Chart;
    chartId: string;
    initFrame: number;
    viewInitted: boolean = false;
    loading: boolean = false;
    loadingSubscriber: Subscription;
    dataSubscriptions: Subscription[] = [];

    @ViewChild('chartElement', { static: true }) chartElement: ElementRef;

    constructor(
        public reportsSeriesDataMergerService: ReportsSeriesDataMergerService,
        public chartAnnotationIndicatorsService: ChartAnnotationIndicatorsService,
        public zone: NgZone,
        @Optional() public reportsSeriesDataGrouperService?: ReportsSeriesDataGrouperService,
    ) {}

    ngAfterViewInit() {
        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            this.viewInitted = true;
            this.initializeChart();
        });
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.initFrame);
        this.destroyOldChart();
    }

    /**
     * Removes the old chart from the DOM, then waits 1 frame before calling destroy.
     * This is faster because when amcharts is destroying a chart, it does a lot of DOM manipulation
     * and it is a lot faster if it's not part of the document.
     */
    destroyOldChart() {
        this.unwatchLoading();
        this.unwatchData();
        if (this.chart) {
            this.chart.destroy();
        }
    }

    public initializeChart(): void {
        if (!this.viewInitted) {
            return;
        }

        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            if (this.chart) {
                // eslint-disable-next-line max-len
                // HACK: for reports drilldown, whenever we change the source we have to create a new chart, and it always adds the new chart instead of replacing it
                while (this.chartElement.nativeElement.firstChild) {
                    this.chartElement.nativeElement.removeChild(this.chartElement.nativeElement.lastChild);
                }
                this.zone.runOutsideAngular(() => {
                    this.chart.initChart(this.chartElement.nativeElement, this.chart.chartType);
                    this.watchData(this.chart);
                    this.watchLoading();

                    this.chart.tooltipLinkClicked$.subscribe(data => this.tooltipLinkClick.emit({ data: data, element: this.chartElement.nativeElement }));
                });
            }
        });
    }

    /**
     * Gets data from the series to display into the chart
     */
    public watchData(chart: Am5Chart): void {
        const data = this.series.map(item => null);
        const previousData = this.series.map(item => null);
        const erroredSeries = this.series.map(() => false);

        this.series.forEach((item, index) => {
            let previousPeriodDataProvider$: Observable<any>;
            const currentPeriodDataProvider$: Observable<any> = item.dataProvider.pipe(
                filter(v => !!v),
                map(itemData => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = itemData;
                        if (chart.overrideNoContent) {
                            // sometimes this is needed because there is no previousDataSeries but existin in useNewDataMappingWorkflow
                            if (chart.isContentEmpty && chart.isContentEmpty()) {
                                this.noContent.emit(true);
                            } else {
                                this.noContent.emit(false);
                            }
                        }

                        return of(data);
                    });
                }),
                catchError(err => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                        }
                    });
                    return throwError(() => new Error(err));
                })
            );
            if (item.hasPreviousPeriod) {
                previousPeriodDataProvider$ = item.previousPeriodDataProvider.pipe(
                    filter(v => !!v),
                    map((itemData) => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = itemData;
                            return of(previousData);
                        });
                    }),
                    catchError(err => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = [];
                            erroredSeries[index] = true;
                            if (!erroredSeries.includes(false)) {
                                this.noData.emit(true);
                            }
                        });
                        return throwError(() => new Error(err));
                    })
                );
            } else {
                previousPeriodDataProvider$ = of([]);
            }
            this.dataSubscriptions.push(combineLatest([
                currentPeriodDataProvider$,
                previousPeriodDataProvider$
            ]).subscribe({
                next: ([currentPeriodData, previousPeriodData]) => {
                    if (data.every(x => !!x) && (!item.hasPreviousPeriod || previousData.every(x => !!x))) {
                        const combinedData = [item.hasPreviousPeriod ? previousData : [], data].flat() as unknown as DataPointModel[][];
                        const normalizedData = this.reportsSeriesDataGrouperService
                            ? this.reportsSeriesDataGrouperService.normalizeScale(combinedData, chart)
                            : combinedData;
                        const mergedData = this.reportsSeriesDataMergerService.mergeSeriesData(
                            normalizedData,
                            chart,
                            this.series
                        )[0];
                        if (chart instanceof Am5DateValueXYChart && this.reportsSeriesDataGrouperService) {
                            // keep the data points before they are grouped to fit the chart for export
                            const ungroupedData = this.reportsSeriesDataGrouperService.normalizeScale(combinedData, chart, false);
                            chart.ungroupedExportData = this.reportsSeriesDataMergerService.mergeSeriesData(ungroupedData, chart, this.series)[0];
                        }
                        this.chart.series.forEach((s, i) => {
                            if (s.calculateIndicators) {
                                s.calculateIndicators(mergedData, 'value' + s.indicatorTargetIndex, 'value' + i);
                            }
                        });
                        this.chartAnnotationIndicatorsService.calculateIndicatorsForGroupedData(this.chart);
                        chart.updateData(mergedData.slice());
                        chart.updateIndicatorData(mergedData.slice());
                        if (chart.isContentEmpty && chart.isContentEmpty()) {
                            this.noContent.emit(true);
                        } else {
                            this.noContent.emit(false);
                        }
                    }
                },
                error: (error) => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        previousData[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                        }
                    });
                }
            }));
        });
    }

    unwatchData(): void {
        this.dataSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.dataSubscriptions = [];
    }

    async setChartAnnotations(): Promise<void> {
        const area = await firstValueFrom(this.chart.annotatorConfigured$.pipe(
            filter(x => !!x),
            switchMap(x => this.chart.annotator.getMarkerArea()),
            take(1)
        ));
        area.availableMarkerTypes = Object.values(ChartAnnotationMarkerTypesEnum);
        if (this.chart.settings.chartAnnotations) {
            this.chart.annotator.set('markerState', this.chart.settings.chartAnnotations.markerState);
            this.updateChartAnnotations(
                this.chart.settings.chartAnnotations.markerState,
                this.chart.settings.chartAnnotations.indicators,
                false
            );
            // wait enough time for annotations to be painted
            await this.chart.annotator.waitForBake();
        }
    }

    updateChartAnnotations(markerState: any, indicators: any[], updateMarkerState: boolean = true): void {
        if (!this.chart || this.chart.destroyed) {
            return;
        }

        this.chart.settings.chartAnnotations.markerState = markerState;
        this.chart.settings.chartAnnotations.indicators = indicators;

        if (updateMarkerState) {
            this.chart.annotator?.getMarkerArea().then(area => {
                area.availableMarkerTypes = Object.values(ChartAnnotationMarkerTypesEnum);
                this.chart.annotator.set('markerState', markerState);
            });
        }

        if (indicators) {
            const originalChartSeries = this.chart.series.filter(x => !x.indicatorTargetIndex && x.indicatorTargetIndex !== 0);
            this.chartAnnotationIndicatorsService.updateIndicators(this.chart, originalChartSeries, indicators?.map(x => x.value));
            requestAnimationFrame(() => this.series.forEach(series => series.dataProvider.next(series.dataProvider.getValue())));
        }
    }

    /**
     * Start watching the loading state of series data
     */
    public watchLoading(): void {
        if (!this.chart || !this.series?.length) {
            this.loading = false;
            return;
        }

        const seriesProviders = this.series.map((seriesItem) => {
            return seriesItem.dataProvider;
        });

        if (this.showLoadingUntil === ChartLoadingOptionsEnum.all) {
            this.loading = true;
            this.loadingSubscriber = combineLatest(seriesProviders).subscribe({
                // watchLoading is set up inside zone.runOutsideAngular, and the data providers can
                // emit outside Angular's zone (e.g. a chart whose request resolves after change
                // detection has gone idle), so re-enter the zone to make the loading flag update
                // actually re-render — otherwise the spinner stays up until the next CD pass.
                next: (data) => this.zone.run(() => this.loading = data.some((item) => !item)),
                error: () => this.zone.run(() => this.loading = false)
            });
        } else if (this.showLoadingUntil === ChartLoadingOptionsEnum.any) {
            this.loading = true;
            this.loadingSubscriber = combineLatest(seriesProviders).subscribe({
                next: (data) => this.zone.run(() => this.loading = !data.some((item) => !!item)),
                error: () => this.zone.run(() => this.loading = false)
            });
        } else {
            this.loading = false;
        }
    }

    /**
     * Stops watching the loading state of series data
     */
    public unwatchLoading(): void {
        this.loading = false;
        if (this.loadingSubscriber) {
            this.loadingSubscriber.unsubscribe();
        }
    }
}
